import { useState, useContext } from 'react';
import { Box, Paper, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
    AppContext,
    SttFormText,
    SttButton,
    SttFormPrice,
} from '../../../sporttia/all';
import { formatPriceByLocale, getErrorMessage } from '../../../lib/utils';
import DataphonePaymentDialog from '../../../components/dialogs/DataphonePaymentDialog';
import POSStripePaymentDialog from '../../../components/stripe/POSStripePaymentDialog';
import constants from '../../../config/constants';
import useProductsService from '../../../services/ProductsService';
import ModalConfirmation from '../../../layout/ModalConfirmation';
import { useLoader } from '../../../lib/hooks';
import { PaymentForm, PosPaymentParams } from '../../../types/payment';
import type { ProductTicket } from '../../../types/models/ProductTicket';
import type { Mship } from '../../../types/models/Mship';
import CashdroControlDialog from '../../../components/dialogs/CashdroControlDialog';

type ReturnParams = {
    productTicket: ProductTicket;
};

type SalePointPaymentProps = {
    ticket: ProductTicket;
    mship: Mship;
    total: number;
    onUpdateTicket: (ticket: ProductTicket) => void;
    onReset: (closedTicket: ProductTicket) => void;
};

export default function SalePointPayment({
    ticket,
    mship,
    total,
    onUpdateTicket,
    onReset,
}: SalePointPaymentProps) {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();

    const [cashIn, setCashIn] = useState(0);
    const [posPaymentParams, setPosPaymentParams] =
        useState<PosPaymentParams<ReturnParams> | null>(null);

    const [selectedPaymentForm, setSelectedPaymentForm] = useState<
        PaymentForm | string
    >();

    const [ticketForDelayedUpdate, setTicketForDelayedUpdate] =
        useState<ProductTicket | null>();

    const [cashdroPayment, setCashdroPayment] = useState(false);
    const [cashdroPaymentAmount, setCashdroPaymentAmount] = useState(0);

    const CASHDRO = cxt?.sc?.cashdroConnected;

    const rest = parseInt(cashIn.toString()) > 0 ? cashIn - total : 0;

    const productsServices = useProductsService();
    const closeProductTicketMutation = productsServices.useCloseProductTicket({
        onSuccess: (result: any) => {
            const { tpv, productTicket } = result;

            // Pay with TPV
            if (tpv) {
                if (
                    tpv.payment?.paymentForm ===
                    constants.payment.paymentForms.dataphoneConnected.name
                ) {
                    // params for the POS dialog (datáfono físico)
                    setPosPaymentParams({
                        amount: tpv.price ?? 0,
                        concept: tpv.concept ?? '',
                        idTpv: tpv.id ?? 0,
                        returnParams: {
                            productTicket,
                        },
                        sc: {
                            id: tpv?.sc?.id,
                            scName: tpv?.sc?.short,
                            entityName: tpv?.sc?.customer?.name,
                            address: tpv?.sc?.address,
                            cif: tpv?.sc?.customer?.cif,
                            phone: tpv?.sc?.phonePublic,
                        },
                    });
                }
            } else {
                cxt.showMessage('S', t('generic.ticketClosed'));
                onUpdateTicket(productTicket);
                onReset(productTicket);
            }
        },
        onError: (error: any) => {
            cxt.showMessage('E', getErrorMessage(error));
        },
    });

    const setupPayment = () => {
        if (
            CASHDRO &&
            selectedPaymentForm === constants.payment.paymentForms.cashdro.name
        ) {
            setCashdroPayment(true);
            setCashdroPaymentAmount(total);
        } else {
            closeTicket(null);
        }
    };

    const closeTicket = (amount: number | null) => {
        closeProductTicketMutation.mutate({
            id: ticket.id,
            params: {
                paymentForm:
                    selectedPaymentForm ===
                    constants.payment.paymentForms.later.name
                        ? constants.payment.pendingPaymentForm
                        : (selectedPaymentForm as any),
                paid: amount || cashIn,
                idUser: mship?.user?.id,
            },
        } as any);

        setSelectedPaymentForm(undefined);
    };

    const cashdroPaymentSuccess = () => {
        closeTicket(total);
    };

    const [, loader] = useLoader([closeProductTicketMutation.status]);

    return (
        <>
            {loader}
            <Box className="mt-6">
                <Paper>
                    <Box className="p-6">
                        <Box className="mb-4">
                            <Typography variant="h5">
                                {t('generic.collect')}
                            </Typography>
                        </Box>

                        <Grid container spacing={3}>
                            <SttFormText
                                grid
                                disabled
                                md={4}
                                name="name"
                                caption={t('generic.total')}
                                defVal={formatPriceByLocale(
                                    total,
                                    cxt.sc!.city!.country!.currency,
                                )}
                            />

                            <SttFormPrice
                                grid
                                md={4}
                                name="cashIn"
                                caption={t('CashIn')}
                                defVal={String(cashIn)}
                                onChange={({ value }: any) => {
                                    const parsedValue = Number(value);

                                    if (Number.isNaN(parsedValue)) {
                                        return;
                                    }

                                    setCashIn(parsedValue);
                                }}
                            />

                            <SttFormText
                                grid
                                disabled
                                md={4}
                                name="rest"
                                caption={t('Rest')}
                                defVal={formatPriceByLocale(
                                    rest,
                                    cxt.sc!.city!.country!.currency,
                                )}
                            />
                        </Grid>

                        {[
                            constants.payment.paymentForms.cash,
                            constants.payment.paymentForms.purse,
                            cxt?.sc?.dataphoneConnected
                                ? constants.payment.paymentForms
                                      .dataphoneConnected
                                : constants.payment.paymentForms.dataphone,
                            constants.payment.paymentForms.later,
                            constants.payment.paymentForms.bank,
                        ]
                            .concat(
                                CASHDRO
                                    ? ([
                                          constants.payment.paymentForms
                                              .cashdro,
                                      ] as any)
                                    : [],
                            )
                            .map((paymentForm: any) => (
                                <Box key={paymentForm.name} className="mt-4">
                                    <SttButton
                                        fullWidth
                                        variant="contained"
                                        color="primary"
                                        caption={t(paymentForm.translationCode)}
                                        onClick={() => {
                                            setSelectedPaymentForm(
                                                paymentForm.name,
                                            );
                                        }}
                                    />
                                </Box>
                            ))}
                    </Box>
                </Paper>
            </Box>

            {selectedPaymentForm && (
                <ModalConfirmation
                    show
                    onAccept={setupPayment}
                    onClose={() => {
                        setSelectedPaymentForm(undefined);
                    }}
                />
            )}

            {cxt.sc?.sporttiaStripeLocationId && posPaymentParams !== null ? (
                <POSStripePaymentDialog
                    paymentData={posPaymentParams}
                    onClose={() => setPosPaymentParams(null)}
                    onSuccess={() => {
                        const { productTicket } = posPaymentParams.returnParams;
                        cxt.showMessage('S', t('generic.ticketClosed'));
                        onUpdateTicket(productTicket);
                        onReset(productTicket);
                    }}
                />
            ) : (
                <DataphonePaymentDialog
                    open={posPaymentParams !== null}
                    paymentData={posPaymentParams}
                    onClose={() => {
                        setPosPaymentParams(null);
                        if (ticketForDelayedUpdate) {
                            onUpdateTicket(ticketForDelayedUpdate);
                            onReset(ticketForDelayedUpdate);
                            setTicketForDelayedUpdate(null);
                        }
                    }}
                    onSuccess={
                        ((_tpvResult: unknown, returnParams: ReturnParams) => {
                            const { productTicket } = returnParams;
                            cxt.showMessage('S', t('generic.ticketClosed'));

                            setTicketForDelayedUpdate(productTicket);
                        }) as any
                    }
                />
            )}

            {cashdroPayment && (
                <CashdroControlDialog
                    open={cashdroPayment}
                    amount={cashdroPaymentAmount}
                    onClose={() => {
                        setCashdroPayment(false);
                        setCashdroPaymentAmount(0);
                    }}
                    onSuccess={() => {
                        setCashdroPayment(false);
                        setCashdroPaymentAmount(0);
                        cashdroPaymentSuccess();
                    }}
                />
            )}
        </>
    );
}
